import { SourceOrder } from 'smg/cart-items';
import i18n from '../i18n';
import Big from 'big.js';
import * as models from 'smg/admin/models';
import { ShortURL } from 'smg/admin/short';
import axios from 'smg/axios';
import * as clipboard from 'smg/clipboard';
import { ShowError } from 'smg/errors';
import { Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { Debounce, DollarFormat } from 'smg/util';
import * as cartID from '../cart/cartid';
import { OpenPriceBreakdown } from '../cart/pricebreakdown';
import { ShortProductName } from '../cart/productnames';
import type { LineItemEditorHandle } from './lineitemeditor';

declare interface myWindow extends Window {
    readonly CloudFront: string;
}
declare const window: myWindow;

export interface SummaryHandle {
    // Recompute totals for the given shipping address. Pass undefined to clear
    // the address (shipping recomputes to its base; tax falls back to $0). The
    // call is debounced so noisy input events from typing coalesce into one
    // request.
    refresh(addrInit: models.IAddress | undefined): void;
    // Override the tax line to $0 (exempt) without re-fetching. Shipping/
    // subtotal continue to track the address. The actual exemption is applied
    // server-side at order create — this is just the visual mirror so the
    // total reflects the admin's selection.
    setExempt(exempt: boolean): void;
    // Apply a promotional code to the cart fetch. The admin /carts/:id
    // handler looks the code up and computes the discount; it flows back as
    // the cart's `discount` field, rendered in the discount row. Empty
    // string clears the promo.
    setPromoCode(code: string): void;
    // Force the cart's shipping to a specific amount on the next fetch.
    // Server-side substitutes this before tax computation so jurisdictions
    // that tax shipping recompute against the override too. Pass undefined
    // to clear and fall back to the computed shipping.
    setShippingOverride(override: InstanceType<typeof Big> | undefined): void;
    // Last shipping override the admin set, or undefined if there isn't one.
    // The place-order submit reads this so the POST /orders body carries the
    // same value the GET /carts/:id preview used to compute the displayed
    // total — server enforces it on the persisted shipping line.
    getShippingOverride(): InstanceType<typeof Big> | undefined;
    // The currently displayed total, including the exempt override. Used by
    // the payment module to validate that split-payment amounts sum to the
    // order total. Returns Big(0) when the cart is empty.
    getTotal(): InstanceType<typeof Big>;
    // Subscribe to total changes (post-renderTotals). Lets payment refresh
    // its split warning when shipping/tax move the total without any
    // payment input event. Registered post-construction so we don't have
    // to forward-reference payment from summary deps.
    onTotalChange(cb: () => void): void;
    // Subscribe to prepayment-applied changes (post-renderTotals). Fires
    // with the server-capped amount so the prepayment section's "Applied
    // to this order" header tracks shipping/promo moves — the cap is
    // preTotal, so changing the components changes what actually applies.
    onPrepaymentChange(cb: (applied: InstanceType<typeof Big>) => void): void;
    // Per-deposit checkbox selection from the prepayment section.
    // `checkedPaid` is the uncapped sum of the checked deposits' paid
    // amounts; undefined means every deposit is checked — the default —
    // which keeps the server-capped cart `prepayment` untouched so default
    // behavior is byte-for-byte the no-checkbox behavior.
    setPrepaymentSelection(checkedPaid: InstanceType<typeof Big> | undefined): void;
    // Mirror of the user-input account-balance credit applied to the cart.
    // Deliberately user-driven (not auto-applied) so shipping/tax/discount
    // moves don't fight the input — but we still clamp against the live
    // pre-balance total at display time so the cart total never goes < 0.
    setBalanceApplied(applied: InstanceType<typeof Big>): void;
    // The preview needs the customer to price their available discount.
    // Refetches when the id changes.
    setCustomer(userID: string): void;
    // undefined spends the whole available discount (omitted from the
    // preview). Big(0) spends none. An amount spends up to that. Refetches
    // when the choice changes so the sidebar matches the order POST.
    setAvailableDiscountChoice(apply: InstanceType<typeof Big> | undefined): void;
    // The POST and the payment amounts must come from the preview of the
    // current inputs, and the 500 ms debounce otherwise lets Place Order
    // outrun it. Resolves true once that preview has landed. After a failed
    // fetch it fetches again and resolves false only if that fails too.
    settled(): Promise<boolean>;
    // The cart total before the account-balance credit is subtracted. Lets
    // the balance section's "Apply $X" link compute the right suggested
    // amount on each click. Always >= 0; mirrors the displayed total math.
    getTotalBeforeBalance(): InstanceType<typeof Big>;
    // Surface the "as-if-submitted" payment amount in the summary. Renders
    // a Paid / Remaining row pair beneath the total. Pass undefined when
    // payment isn't applicable (skipped, non-CC type) to hide the row.
    setPaid(paid: InstanceType<typeof Big> | undefined): void;
    // Re-fetch the cart preview after the line item editor commits edits —
    // the fetch carries the editor's lineItemOverrides/lineItemAdditions
    // params, and the response re-renders both the totals and the summary
    // item rows (the server returns the cart with edits applied).
    refreshEdits(): void;
}

export function SetupSummary(cartInit: models.ICartPreviewResponse | undefined, lineItemEditor: LineItemEditorHandle): SummaryHandle {
    const $loading = document.getElementById('summary-loading') as HTMLElement;
    const $empty = document.getElementById('summary-empty') as HTMLElement;
    const $content = document.getElementById('summary-content') as HTMLElement;
    const $items = document.getElementById('summary-items') as HTMLElement;
    const $subtotal = document.getElementById('summary-subtotal') as HTMLElement;
    const $discountRow = document.getElementById('summary-discount-row') as HTMLElement;
    const $discount = document.getElementById('summary-discount') as HTMLElement;
    const $discountCode = document.getElementById('summary-discount-code') as HTMLElement;
    const $availableDiscountRow = document.getElementById('summary-available-discount-row') as HTMLElement;
    const $availableDiscount = document.getElementById('summary-available-discount') as HTMLElement;
    const $shipping = document.getElementById('summary-shipping') as HTMLElement;
    const $tax = document.getElementById('summary-tax') as HTMLElement;
    const $taxRate = document.getElementById('summary-tax-rate') as HTMLElement;
    const $prepaymentRow = document.getElementById('summary-prepayment-row') as HTMLElement;
    const $prepayment = document.getElementById('summary-prepayment') as HTMLElement;
    const $balanceRow = document.getElementById('summary-balance-row') as HTMLElement;
    const $balance = document.getElementById('summary-balance') as HTMLElement;
    const $total = document.getElementById('summary-total') as HTMLElement;
    const $progress = document.getElementById('summary-payment-progress') as HTMLElement;
    const $paid = document.getElementById('summary-paid') as HTMLElement;
    const $remaining = document.getElementById('summary-remaining') as HTMLElement;
    $loading.classList.add('d-none');

    const cart = cartInit ? new models.CartPreviewResponse(cartInit) : undefined;
    if (!cart || cart.cart.cartItems.length === 0) {
        $empty.classList.remove('d-none');
        return {
            refresh: () => undefined,
            setExempt: () => undefined,
            setPromoCode: () => undefined,
            setShippingOverride: () => undefined,
            getShippingOverride: () => undefined,
            getTotal: () => Big(0),
            onTotalChange: () => undefined,
            onPrepaymentChange: () => undefined,
            setPrepaymentSelection: () => undefined,
            setBalanceApplied: () => undefined,
            setCustomer: () => undefined,
            setAvailableDiscountChoice: () => undefined,
            settled: () => Promise.resolve(true),
            getTotalBeforeBalance: () => Big(0),
            setPaid: () => undefined,
            refreshEdits: () => undefined,
        };
    }

    const totalChangeListeners: (() => void)[] = [];
    const prepaymentChangeListeners: ((applied: InstanceType<typeof Big>) => void)[] = [];
    // The user-input balance credit, pre-clamp. Display math clamps against
    // totalBeforeBalance so the row + total never go negative; we keep the
    // raw input here so a balance the admin entered before shipping is set
    // doesn't get silently zeroed by the early-frame clamp.
    let balanceApplied: InstanceType<typeof Big> = Big(0);

    renderItems($items, cart.cart.cartItems, lineItemEditor);
    let lastCart: models.CartPreviewResponse = cart;
    let exempt = false;
    // undefined = payment isn't applicable (skip / non-CC type). Driven
    // by payment.ts via setPaid; renderTotals also re-renders the paid/
    // remaining lines because remaining depends on the changing total.
    let paid: InstanceType<typeof Big> | undefined;
    // Mirror of the last promo code passed to setPromoCode. Used both as a
    // fetch param and as the label rendered in the discount row. Declared
    // above the initial renderTotals() so the TDZ guard doesn't trip.
    let pendingPromo = '';
    // Last shipping override the admin set. Forwarded to the cart preview on
    // every fetch so the server-computed shipping (and tax that depends on it)
    // is replaced by this value. undefined = no override.
    let pendingShippingOverride: InstanceType<typeof Big> | undefined;
    // The prepayment section's per-deposit checkbox selection: the uncapped
    // sum of the checked deposits' paid amounts, or undefined when every
    // deposit is checked (the default) — see setPrepaymentSelection.
    let prepaymentSelectedPaid: InstanceType<typeof Big> | undefined;
    renderTotals();
    $content.classList.remove('d-none');
    // The #place-order-btn stays disabled here — the still-pending
    // payment/place-order wiring is responsible for enabling it once it can
    // actually submit the order. Enabling without a handler would be a
    // misleading no-op for the admin.

    // The pre-prepayment total the server caps the deposit credit against:
    // subtotal + discount + available discount + shipping (+ tax unless
    // exempt), clamped at $0. Mirrors carts.go's preTotal so client-side
    // re-caps agree with it.
    function preTotal(): InstanceType<typeof Big> {
        let t = lastCart.subtotal.plus(lastCart.discount).plus(lastCart.availableDiscount).plus(lastCart.shipping);
        if (!exempt) {
            t = t.plus(lastCart.taxAmount);
        }
        return t.lt(0) ? Big(0) : t;
    }

    // Server caps prepayment against its taxable preTotal, but two client
    // states move the goalposts: exempt mode drops tax (the server cap would
    // let prepayment exceed what the cart actually owes), and unchecking a
    // deposit row shrinks the credit to the checked subset. Re-cap the
    // applicable sum against the live preTotal so every prepayment-touching
    // readout (row, total math, currentTotal, onPrepaymentChange) agrees.
    // The all-checked non-exempt default returns the server value untouched.
    function effectivePrepayment(): InstanceType<typeof Big> {
        if (!exempt && prepaymentSelectedPaid === undefined) {
            return lastCart.prepayment;
        }
        const base = prepaymentSelectedPaid ?? lastCart.prepayment;
        const cap = preTotal();
        return base.gt(cap) ? cap : base;
    }

    // The pre-balance grand total: everything except the account-balance
    // credit. Mirrors the displayed total math (exempt + prepayment
    // selection) so the balance section's Apply link suggests the right
    // amount. The all-checked non-exempt default returns the server total
    // untouched.
    function totalBeforeBalance(): InstanceType<typeof Big> {
        if (!exempt && prepaymentSelectedPaid === undefined) {
            return lastCart.total;
        }
        const t = preTotal().minus(effectivePrepayment());
        return t.lt(0) ? Big(0) : t;
    }

    // The portion of the user-input balance the cart can actually absorb.
    // Clamps the raw input against the pre-balance total so the row + grand
    // total never go negative when shipping/promo moves drop the total below
    // the entered amount.
    function effectiveBalance(): InstanceType<typeof Big> {
        const cap = totalBeforeBalance();
        return balanceApplied.gt(cap) ? cap : balanceApplied;
    }

    function renderTotals() {
        const c = lastCart;
        const prepay = effectivePrepayment();
        const balanceCredit = effectiveBalance();
        $subtotal.textContent = DollarFormat(c.subtotal);
        // Discount comes back negative from the API (added to total). Intl
        // currency formatting renders the leading minus on its own.
        if (c.discount.lt(0)) {
            $discount.textContent = DollarFormat(c.discount);
            $discountCode.textContent = pendingPromo ? `(${pendingPromo})` : '';
            $discountRow.classList.remove('d-none');
        } else {
            $discountRow.classList.add('d-none');
        }
        // Available discount comes back negative from the API (added to
        // total), same as the promo discount. Zero is omitted.
        if (c.availableDiscount.lt(0)) {
            $availableDiscount.textContent = DollarFormat(c.availableDiscount);
            $availableDiscountRow.classList.remove('d-none');
        } else {
            $availableDiscountRow.classList.add('d-none');
        }
        $shipping.textContent = DollarFormat(c.shipping);
        if (exempt) {
            // Discount lands negative from the API (added to total), so plus
            // here even though it visually reads as a subtraction. Server
            // clamps total at 0; mirror that here.
            $tax.textContent = DollarFormat(Big(0));
            $taxRate.textContent = i18n('exemptParen');
        } else {
            $tax.textContent = DollarFormat(c.taxAmount);
            if (c.taxRate.gt(0)) {
                $taxRate.textContent = `(${c.taxRate.times(100).toFixed(2)}%)`;
            } else {
                $taxRate.textContent = '';
            }
        }
        const grandTotal = totalBeforeBalance().minus(balanceCredit);
        $total.textContent = DollarFormat(grandTotal.lt(0) ? Big(0) : grandTotal);
        // Display as -$X.XX so the row reads as a credit, matching the
        // discount row's visual treatment.
        if (prepay.gt(0)) {
            $prepayment.textContent = DollarFormat(Big(0).minus(prepay));
            $prepaymentRow.classList.remove('d-none');
        } else {
            $prepaymentRow.classList.add('d-none');
        }
        if (balanceCredit.gt(0)) {
            $balance.textContent = DollarFormat(Big(0).minus(balanceCredit));
            $balanceRow.classList.remove('d-none');
        } else {
            $balanceRow.classList.add('d-none');
        }
        renderProgress();
        for (const cb of totalChangeListeners) {
            cb();
        }
        for (const cb of prepaymentChangeListeners) {
            cb(prepay);
        }
    }

    function spinner(): HTMLElement {
        const $s = document.createElement('span');
        $s.className = 'spinner-border spinner-border-sm';
        $s.setAttribute('role', 'status');
        $s.setAttribute('aria-hidden', 'true');
        return $s;
    }

    // Called the moment a fetch is queued (before the 500ms debounce) so the
    // discount/shipping/tax rows reflect that they're recomputing — the user
    // sees feedback without waiting for the network. renderTotals() naturally
    // replaces these with the resolved values when the response lands.
    function showLoadingTotals() {
        $shipping.replaceChildren(spinner());
        $tax.replaceChildren(spinner());
        $taxRate.textContent = '';
        if (pendingPromo) {
            $discount.replaceChildren(spinner());
            $discountCode.textContent = `(${pendingPromo})`;
            $discountRow.classList.remove('d-none');
        }
        // Prepayment caps at the pre-prepayment total, so shipping/tax/discount
        // moves can change it. Show the spinner only if a prepayment was
        // already in play so we don't flash a row that ends up empty.
        if (lastCart.prepayment.gt(0)) {
            $prepayment.replaceChildren(spinner());
            $prepaymentRow.classList.remove('d-none');
        }
        // Shipping and tax moves can change how much of the discount the
        // goods total can absorb. Spin only a row that was already showing
        // a non-zero value so an empty row doesn't flash.
        if (lastCart.availableDiscount.lt(0)) {
            $availableDiscount.replaceChildren(spinner());
        }
    }

    function renderProgress() {
        if (paid === undefined) {
            $progress.classList.add('d-none');
            return;
        }
        const total = currentTotal();
        $paid.textContent = DollarFormat(paid);
        $remaining.textContent = DollarFormat(total.minus(paid));
        $progress.classList.remove('d-none');
    }

    function currentTotal(): InstanceType<typeof Big> {
        // Mirror the displayed grand total (renderTotals): pre-balance total
        // minus the clamped balance credit, clamped at 0. Payment split
        // validation reads this, so any drift here would make the form
        // validate against a number the admin can't see.
        const t = totalBeforeBalance().minus(effectiveBalance());
        return t.lt(0) ? Big(0) : t;
    }

    let fetchSeq = 0;
    // The seq whose result is on screen. A bump leaves this behind until
    // fetchTotals for the new seq lands, which is what settled() waits on.
    let settledSeq = 0;
    // Whether that fetch succeeded. A failed preview keeps its stale totals on
    // screen, so it must keep failing settled() until a new fetch lands.
    let settledOK = true;
    const settleWaiters: ((ok: boolean) => void)[] = [];
    let pendingAddr: models.Address | undefined;
    // Customer the available-discount preview is priced for. Empty omits
    // userID, so the server applies no discount.
    let customerUserID = '';
    // undefined spends all available discount. A Big is sent as
    // applyAvailableDiscount (0 spends none).
    let availableDiscountChoice: InstanceType<typeof Big> | undefined;

    function settle(mySeq: number, ok: boolean) {
        if (mySeq !== fetchSeq) {
            return;
        }
        settledSeq = mySeq;
        settledOK = ok;
        const waiters = settleWaiters.splice(0, settleWaiters.length);
        for (const waiter of waiters) {
            waiter(ok);
        }
    }

    // A failed preview is retried here rather than reported as failed forever:
    // a Place Order click fetches the current inputs again and waits for them.
    function settled(): Promise<boolean> {
        if (settledSeq === fetchSeq) {
            if (settledOK) {
                return Promise.resolve(true);
            }
            fetchSeq++;
            showLoadingTotals();
            void fetchTotals();
        }
        return new Promise(resolve => {
            settleWaiters.push(resolve);
        });
    }

    async function fetchTotals() {
        const id = cartID.Get();
        // Don't bump here — the caller already did. We just capture the current
        // token, so if a later caller bumps it while this request is in
        // flight, we discard our stale response on return.
        const mySeq = fetchSeq;
        // No cart to preview. Treat it as settled so Place Order isn't stuck
        // on a fetch that will never start.
        if (!id) {
            settle(mySeq, true);
            return;
        }

        const params: Record<string, string | number> = {
            data: 'id,name,active,ordered,user,cartItems',
        };
        if (pendingAddr) {
            params['addressLine1'] = pendingAddr.addressLine1;
            params['addressLine2'] = pendingAddr.addressLine2;
            params['city'] = pendingAddr.city;
            params['stateID'] = pendingAddr.stateID;
            params['postalCode'] = pendingAddr.postalCode;
            params['countryID'] = pendingAddr.countryID;
        }
        if (pendingPromo) {
            params['promo'] = pendingPromo;
        }
        if (pendingShippingOverride !== undefined) {
            params['shippingOverride'] = pendingShippingOverride.toFixed(2);
        }
        if (customerUserID) {
            params['userID'] = customerUserID;
        }
        if (availableDiscountChoice !== undefined) {
            params['applyAvailableDiscount'] = availableDiscountChoice.toFixed(2);
        }
        // Line item edits ride every preview fetch so the server-computed
        // subtotal/discount/tax (and the serialized cart items themselves)
        // reflect them — the same values the place-order POST will carry.
        Object.assign(params, lineItemEditor.params());

        try {
            const resp = await axios.get(`/v2/carts/${id}`, { params });
            if (mySeq !== fetchSeq) {
                return;
            }
            lastCart = models.CartPreviewResponse.fromJSON(resp.data);
            renderTotals();
            // The response cart carries the edited line items (the server
            // applied the overrides before serializing), so re-rendering the
            // item rows updates names, piece counts, and per-item prices.
            renderItems($items, lastCart.cart.cartItems, lineItemEditor);
            settle(mySeq, true);
        } catch (err) {
            if (mySeq !== fetchSeq) {
                return;
            }
            ShowError(err);
            // Restore the previous lastCart values so the spinners don't
            // stick around forever after a network failure.
            renderTotals();
            settle(mySeq, false);
        }
    }

    const refreshDebounced = Debounce(() => void fetchTotals(), 500, false);

    return {
        refresh: addrInit => {
            pendingAddr = addrInit ? new models.Address(addrInit) : undefined;
            // Bump immediately so any fetch already in flight (started by an
            // earlier debounced call) will see a mismatched seq when it
            // returns and skip the render. The next debounced fetchTotals
            // captures this fresh value.
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
        setExempt: v => {
            if (exempt === v) {
                return;
            }
            exempt = v;
            renderTotals();
        },
        setPromoCode: code => {
            if (pendingPromo === code) {
                return;
            }
            pendingPromo = code;
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
        setShippingOverride: next => {
            const same =
                (pendingShippingOverride === undefined && next === undefined) ||
                (pendingShippingOverride !== undefined && next !== undefined && pendingShippingOverride.eq(next));
            if (same) {
                return;
            }
            pendingShippingOverride = next;
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
        getShippingOverride: () => pendingShippingOverride,
        getTotal: () => currentTotal(),
        onTotalChange: cb => {
            totalChangeListeners.push(cb);
        },
        onPrepaymentChange: cb => {
            prepaymentChangeListeners.push(cb);
        },
        setPrepaymentSelection: checkedPaid => {
            const same =
                (prepaymentSelectedPaid === undefined && checkedPaid === undefined) ||
                (prepaymentSelectedPaid !== undefined && checkedPaid !== undefined && prepaymentSelectedPaid.eq(checkedPaid));
            if (same) {
                return;
            }
            prepaymentSelectedPaid = checkedPaid;
            renderTotals();
        },
        setBalanceApplied: applied => {
            if (balanceApplied.eq(applied)) {
                return;
            }
            balanceApplied = applied;
            renderTotals();
        },
        setCustomer: userID => {
            if (customerUserID === userID) {
                return;
            }
            customerUserID = userID;
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
        setAvailableDiscountChoice: apply => {
            const same =
                (availableDiscountChoice === undefined && apply === undefined) ||
                (availableDiscountChoice !== undefined && apply !== undefined && availableDiscountChoice.eq(apply));
            if (same) {
                return;
            }
            availableDiscountChoice = apply;
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
        settled,
        getTotalBeforeBalance: () => totalBeforeBalance(),
        setPaid: v => {
            // Payment fires onPaymentChange on every keystroke; most of
            // those don't move the resolved paid amount. Guard so the
            // paid/remaining DOM only repaints when it actually changes.
            if (paid === v || (paid !== undefined && v !== undefined && paid.eq(v))) {
                return;
            }
            paid = v;
            renderProgress();
        },
        refreshEdits: () => {
            fetchSeq++;
            showLoadingTotals();
            refreshDebounced();
        },
    };
}

// Sets a summary link's text and appends the trailing external-link icon (the
// link opens in a new tab). Used for the IAP and customer-site links.
function appendLinkLabel($link: HTMLAnchorElement, label: string) {
    $link.textContent = label;
    const $icon = document.createElement('i');
    $icon.className = 'fas fa-external-link-alt ms-1';
    $icon.style.fontSize = '.8em';
    $link.appendChild($icon);
}

function renderItems($items: HTMLElement, cartItems: models.CartItem[], lineItemEditor: LineItemEditorHandle) {
    const f = document.createDocumentFragment();

    for (const cartItem of cartItems) {
        const $el = template.Clone('#checkout-summary-item-template').firstElementChild as HTMLElement;
        const quote = cartItem.quote;
        const order = SourceOrder(cartItem);
        // Product line items back both quotes and reorders; either supplies the
        // product name + the per-product piece counts below.
        const productLineItems = quote?.quoteLineItems ?? order?.orderLineItems ?? [];
        const firstLineItem = productLineItems[0];

        // Reorder (order-backed) line items leave the custom lineItem/type
        // columns empty — the canonical name lives on the joined catalog
        // models — so fall back to those (brand override first, then base),
        // mirroring the pieceCount resolution below. Size falls back to
        // LineItemTypeSize.getName() (its _Name, else readable width×height).
        const productName = firstLineItem?.lineItem || firstLineItem?.lineItemModel?.networkLineItem?.name || firstLineItem?.lineItemModel?.name;
        const productType = firstLineItem?.type || firstLineItem?.lineItemType?.networkLineItemType?.name || firstLineItem?.lineItemType?.name;
        const productSize = firstLineItem?.size || firstLineItem?.lineItemTypeSize?.getName();
        const parts = [productName, productType, productSize].filter(Boolean);

        // Piece count broken out by product — "150 patches", or "100 patches,
        // 50 pins" when mixed — summing product line items only so discounts and
        // setup fees don't inflate it. Stock/upload items use the cart quantity.
        const productQuantities = new Map<string, { name: string; pluralName: string; quantity: number }>();
        for (const lineItem of productLineItems) {
            if (!lineItem.lineItemModel?.product || !lineItem.quantity) {
                continue;
            }
            const model = lineItem.lineItemModel;
            const key = model.id || model.pluralName || model.name || '';
            const entry = productQuantities.get(key);
            if (entry) {
                entry.quantity += lineItem.quantity;
            } else {
                // Prefer the per-network name so the count shortens in context —
                // a brand can name "Lapel Pins" just "Pins" via its
                // networkLineItem override (same resolution as gallery.ts) —
                // falling back to the generic line item name when there's none.
                productQuantities.set(key, {
                    name: model.networkLineItem?.name || model.name || '',
                    pluralName: model.networkLineItem?.pluralName || model.networkLineItem?.name || model.pluralName || model.name || '',
                    quantity: lineItem.quantity,
                });
            }
        }
        let pieces = Array.from(productQuantities.values()).
            map(p => `${p.quantity.toLocaleString()} ${ShortProductName(p.quantity === 1 ? p.name : p.pluralName).toLowerCase()}`).
            join(', ');
        if (!pieces) {
            pieces = `${(cartItem.quantity ?? 0).toLocaleString()} pcs`;
        }
        const serial = quote?.serial;

        ($el.querySelector('.summary-item-product') as HTMLElement).textContent = parts.join(' – ');
        let meta = `${pieces}${serial ? ` &middot; <code class="small">${serial}</code>` : ''}`;
        if (quote && cartItem.order) {
            meta += `<span class="small text-muted ms-2">${i18n('reorderOfN').replace('%s', String(cartItem.order.invoiceNumber ?? ''))}</span>`;
        }
        ($el.querySelector('.summary-item-meta') as HTMLElement).innerHTML = meta;
        ($el.querySelector('.summary-item-price') as HTMLElement).textContent =
            DollarFormat(cartItem.subtotal ?? 0);

        // Info button opens the shared cart-item price breakdown.
        ($el.querySelector('.summary-item-breakdown-btn') as HTMLButtonElement).addEventListener('click', () => {
            OpenPriceBreakdown(cartItem);
        });

        // Pencil → line item editor modal (quote/order-backed items only —
        // stock and upload items carry no addressable quote/order lines).
        // Opened by id: the editor renders from its pristine page-load
        // models, not this possibly-already-edited preview cart item.
        const $editBtn = $el.querySelector('.summary-item-edit-btn') as HTMLButtonElement;
        if (quote || order) {
            $editBtn.addEventListener('click', () => {
                lineItemEditor.open(cartItem.id);
            });
        } else {
            $editBtn.remove();
        }
        if (lineItemEditor.hasEdits(cartItem.id)) {
            ($el.querySelector('.summary-item-edited-badge') as HTMLElement).classList.remove('d-none');
        }

        // Open-in-IAP / open-on-customer-site links — only for quote- and
        // order-backed items (uploads/stock items have no such pages yet). The
        // IAP copy button copies the link as-is; the customer copy button runs
        // it through the URL shortener first (mirrors the copy-short links in
        // getquote.php / order.php). The customer row is dropped when the brand
        // has no customer-URL template configured.
        let iapURL = '';
        let customerURL = '';
        let realmName = '';
        let companyID = '';
        if (quote) {
            iapURL = quote.iapURL();
            customerURL = quote.customerURL();
            realmName = quote.realm?.name ?? '';
            companyID = quote.realm?.id ?? '';
        } else if (order) {
            iapURL = order.iapURL();
            customerURL = order.customerURL();
            realmName = order.realm?.name ?? '';
            companyID = order.realm?.id ?? '';
        }
        if (iapURL) {
            ($el.querySelector('.summary-item-links') as HTMLElement).classList.remove('d-none');

            const $iapLink = $el.querySelector('.summary-item-iap-link') as HTMLAnchorElement;
            $iapLink.href = iapURL;
            appendLinkLabel($iapLink, 'Open in IAP');
            const $iapCopy = $el.querySelector('.summary-item-iap-copy') as HTMLButtonElement;
            $iapCopy.addEventListener('click', () => clipboard.Copy(iapURL, $iapCopy));

            if (customerURL) {
                const $customerLink = $el.querySelector('.summary-item-customer-link') as HTMLAnchorElement;
                $customerLink.href = customerURL;
                appendLinkLabel($customerLink, `Open on ${realmName || 'customer site'}`);
                const $customerCopy = $el.querySelector('.summary-item-customer-copy') as HTMLButtonElement;
                $customerCopy.addEventListener('click', () => {
                    void ShortURL(customerURL, companyID).then(short => clipboard.Copy(short, $customerCopy));
                });
            } else {
                ($el.querySelector('.summary-item-customer-row') as HTMLElement).remove();
            }
        }

        const proof = quote?.quoteProofs?.[0]?.proof ?? order?.orderProofs?.[0]?.proof;
        const proofFile = proof?.file;
        const $proofLink = $el.querySelector('.summary-item-proof-link') as HTMLAnchorElement;
        const $proofImg = $el.querySelector('.summary-item-proof') as HTMLImageElement;
        if (proofFile) {
            const cloudFront = quote?.realm?.cloudFront || order?.realm?.cloudFront || window.CloudFront;
            const urls = GetProofURLs(cloudFront, proofFile);
            $proofLink.href = urls.main;
            void Picture($proofImg, urls.thumb, { displayWidth: 48, moderationLabels: proof?.moderationLabels });
        } else {
            $proofLink.remove();
        }

        f.appendChild($el);
    }

    $items.replaceChildren(f);
}
